import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FeatureLimitHint } from '../../../components/FeatureLimitHint';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { useFeatureLimits } from '../../../hooks/useFeatureLimits';
import { AppSelectDropdown, type AppSelectOption } from '../../../components/AppSelectDropdown';
import { AppSelect } from '../../../components/AppSelect';
import {
  LEAD_CATEGORY_COLOR_OPTIONS,
  defaultLeadCategoryColorForIndex,
  normalizeLeadCategoryColor,
} from '../../../utils/leadCategoryColors';
import type { LeadCategoryRow } from '../types';
import type { LeadPipeline } from '../../../types';
import {
  PipelineStageMapModal,
  type PipelineStageMapChange,
  type PipelineStageMapPayload,
} from '../components/PipelineStageMapModal';
import { SettingsCard } from '../components/SettingsCard';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

type LeadCategoriesSettingsSectionProps = {
  /** Live pipeline list from LeadPipelineSettingsSection (keeps picker in sync). */
  pipelines?: LeadPipeline[];
  /** Notify siblings (e.g. pipeline assignment chips) when categories change. */
  onCategoriesChange?: (categories: LeadCategoryRow[]) => void;
};

function normalizePipelineId(id: string | null | undefined, defaultId: string): string {
  return id && String(id).trim() ? String(id) : defaultId;
}

function serializeCategories(cats: LeadCategoryRow[], defaultPipelineId: string): string {
  return JSON.stringify(
    cats.map((c) => ({
      key: c.key,
      label: c.label.trim(),
      color: c.color,
      pipeline_id: normalizePipelineId(c.pipeline_id, defaultPipelineId),
    })),
  );
}

function mapApiCategories(
  cats: unknown,
  defId: string,
): LeadCategoryRow[] {
  if (!Array.isArray(cats)) return [];
  return cats.map(
    (
      row: {
        id?: string;
        key?: string;
        label?: string;
        sort_order?: number;
        color?: string;
        pipeline_id?: string | null;
      },
      i: number,
    ) => ({
      id: String(row.id ?? row.key ?? `cat_${i}`),
      key: String(row.key ?? row.id ?? `cat_${i}`),
      label: String(row.label ?? ''),
      sort_order: typeof row.sort_order === 'number' ? row.sort_order : i,
      color: normalizeLeadCategoryColor(row.color, i),
      pipeline_id: row.pipeline_id ?? defId ?? null,
    }),
  );
}

function parseCategoriesPayload(
  data: unknown,
  fallback: LeadCategoryRow[],
  defaultPipelineId: string,
): { categories: LeadCategoryRow[]; remappedCount: number } {
  if (Array.isArray(data)) {
    return { categories: mapApiCategories(data, defaultPipelineId), remappedCount: 0 };
  }
  if (data && typeof data === 'object') {
    const obj = data as { categories?: unknown; remapped_leads_count?: unknown };
    const cats = Array.isArray(obj.categories)
      ? mapApiCategories(obj.categories, defaultPipelineId)
      : fallback;
    const remappedCount =
      typeof obj.remapped_leads_count === 'number' && Number.isFinite(obj.remapped_leads_count)
        ? obj.remapped_leads_count
        : 0;
    return { categories: cats, remappedCount };
  }
  return { categories: fallback, remappedCount: 0 };
}

export function LeadCategoriesSettingsSection({
  pipelines: pipelinesProp,
  onCategoriesChange,
}: LeadCategoriesSettingsSectionProps = {}) {
  const [categories, setCategories] = React.useState<LeadCategoryRow[]>([]);
  const [baseline, setBaseline] = React.useState('');
  const [savedCategories, setSavedCategories] = React.useState<LeadCategoryRow[]>([]);
  const [pipelinesLocal, setPipelinesLocal] = React.useState<LeadPipeline[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [successHint, setSuccessHint] = React.useState<string | null>(null);
  const [categoryDraft, setCategoryDraft] = React.useState('');
  const { usage, loaded: limitsReady } = useFeatureLimits();
  const categoryLimit = usage('leadCategories');
  const categoriesAtLimit = categories.length >= categoryLimit.max;
  const [stageMapChanges, setStageMapChanges] = React.useState<PipelineStageMapChange[] | null>(
    null,
  );

  const pipelines = pipelinesProp ?? pipelinesLocal;

  React.useEffect(() => {
    onCategoriesChange?.(categories);
  }, [categories, onCategoriesChange]);

  const colorSelectOptions = React.useMemo<AppSelectOption[]>(
    () =>
      LEAD_CATEGORY_COLOR_OPTIONS.map((opt) => ({
        value: opt.id,
        label: opt.label,
        icon: <span className={`block h-4 w-4 rounded-full ${opt.swatch}`} aria-hidden />,
      })),
    [],
  );

  const defaultPipelineId = pipelines.find((p) => p.is_default)?.id ?? pipelines[0]?.id ?? '';

  const pipelineOptions = React.useMemo(
    () =>
      pipelines.map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [pipelines],
  );

  const isDirty =
    loaded &&
    Boolean(defaultPipelineId) &&
    baseline !== '' &&
    serializeCategories(categories, defaultPipelineId) !== baseline;

  // Pipelines often arrive after categories (shared parent state). Re-normalize and
  // re-baseline once the default pipeline id is known so Speichern stays off.
  const baselinedDefaultIdRef = React.useRef('');
  React.useEffect(() => {
    if (!loaded || !defaultPipelineId) return;
    if (baselinedDefaultIdRef.current === defaultPipelineId) return;
    baselinedDefaultIdRef.current = defaultPipelineId;
    setCategories((prev) => {
      const next = prev.map((c) => ({
        ...c,
        pipeline_id: normalizePipelineId(c.pipeline_id, defaultPipelineId),
      }));
      setSavedCategories(next);
      setBaseline(serializeCategories(next, defaultPipelineId));
      return next;
    });
  }, [loaded, defaultPipelineId]);

  // If a pipeline was deleted, fall categories back to the default pipeline.
  React.useEffect(() => {
    if (pipelines.length === 0 || !defaultPipelineId) return;
    const ids = new Set(pipelines.map((p) => p.id));
    setCategories((prev) => {
      let changed = false;
      const next = prev.map((c) => {
        const pid = normalizePipelineId(c.pipeline_id, defaultPipelineId);
        if (pid && !ids.has(pid)) {
          changed = true;
          return { ...c, pipeline_id: defaultPipelineId };
        }
        return c;
      });
      return changed ? next : prev;
    });
  }, [pipelines, defaultPipelineId]);

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    const loadPipelines = pipelinesProp === undefined;
    Promise.all([
      fetch('/api/settings/lead-categories', { credentials: 'include' }),
      loadPipelines
        ? fetch('/api/settings/lead-pipelines', { credentials: 'include' })
        : Promise.resolve(null),
    ])
      .then(async ([catRes, pipeRes]) => {
        const cats = catRes.ok ? await catRes.json() : [];
        const pipes =
          pipeRes && pipeRes.ok ? await pipeRes.json() : loadPipelines ? [] : pipelinesProp;
        if (cancelled) return;
        if (loadPipelines) {
          setPipelinesLocal(Array.isArray(pipes) ? (pipes as LeadPipeline[]) : []);
        }
        const pipeList = loadPipelines
          ? Array.isArray(pipes)
            ? (pipes as LeadPipeline[])
            : []
          : (pipelinesProp ?? []);
        const defId = pipeList.find((p) => p.is_default)?.id ?? pipeList[0]?.id ?? '';
        const mapped = mapApiCategories(cats, defId);
        setCategories(mapped);
        setSavedCategories(mapped);
        setBaseline(serializeCategories(mapped, defId));
      })
      .catch(() => {
        if (!cancelled) setError('Kategorien konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial load only; live pipelines come via prop

  const detectPipelineChanges = React.useCallback((): PipelineStageMapChange[] => {
    if (!defaultPipelineId || pipelines.length === 0) return [];
    const byKey = new Map(savedCategories.map((c) => [c.key, c]));
    const pipeById = new Map(pipelines.map((p) => [p.id, p]));
    const changes: PipelineStageMapChange[] = [];

    for (const cat of categories) {
      const prev = byKey.get(cat.key);
      if (!prev) continue;
      const fromId = normalizePipelineId(prev.pipeline_id, defaultPipelineId);
      const toId = normalizePipelineId(cat.pipeline_id, defaultPipelineId);
      if (fromId === toId) continue;
      const fromPipeline = pipeById.get(fromId);
      const toPipeline = pipeById.get(toId);
      if (!fromPipeline || !toPipeline) continue;
      changes.push({
        categoryKey: cat.key,
        categoryLabel: cat.label.trim() || cat.key,
        fromPipeline,
        toPipeline,
      });
    }
    return changes;
  }, [categories, savedCategories, defaultPipelineId, pipelines]);

  const persistCategories = async (stageMaps?: PipelineStageMapPayload[]) => {
    setSaving(true);
    setError(null);
    setSuccessHint(null);
    try {
      const categoriesPayload = categories.map((c, i) => ({
        key: c.key,
        label: c.label,
        sort_order: i,
        color: c.color,
        pipeline_id:
          c.pipeline_id && c.pipeline_id !== defaultPipelineId ? c.pipeline_id : null,
      }));
      const res = await fetch('/api/settings/lead-categories', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          categories: categoriesPayload,
          ...(stageMaps && stageMaps.length > 0 ? { stage_maps: stageMaps } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const parsed = parseCategoriesPayload(data, categories, defaultPipelineId);
        setCategories(parsed.categories);
        setSavedCategories(parsed.categories);
        setBaseline(serializeCategories(parsed.categories, defaultPipelineId));
        setStageMapChanges(null);
        if (parsed.remappedCount > 0) {
          setSuccessHint(
            parsed.remappedCount === 1
              ? '1 Projekt auf die neue Pipeline verschoben.'
              : `${parsed.remappedCount} Projekte auf die neue Pipeline verschoben.`,
          );
        }
      } else {
        setError(String((data as { error?: string }).error ?? 'Speichern fehlgeschlagen.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!isDirty || categories.length === 0) return;
    const changes = detectPipelineChanges();
    if (changes.length > 0) {
      setStageMapChanges(changes);
      return;
    }
    await persistCategories();
  };

  const add = () => {
    const label = categoryDraft.trim();
    if (!label || categories.length >= categoryLimit.max) return;
    const id = `custom_${Date.now()}`;
    setCategories((prev) => [
      ...prev,
      {
        id,
        key: id,
        label,
        sort_order: prev.length,
        color: defaultLeadCategoryColorForIndex(prev.length),
        pipeline_id: defaultPipelineId || null,
      },
    ]);
    setCategoryDraft('');
  };

  const remove = (id: string) => {
    setCategories((prev) => prev.filter((c) => c.id !== id));
  };

  const update = (id: string, patch: Partial<LeadCategoryRow>) => {
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    setSuccessHint(null);
  };

  return (
    <>
    <SettingsCard
      title="Projekt-Kategorien"
      description="Kategorien für Filter und neue Projekte — optional mit eigener Pipeline."
    >
      {error ? (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      ) : null}
      {successHint ? (
        <div className="mb-4 p-3 rounded-md bg-sky-50 text-sky-800 text-sm border border-sky-200">
          {successHint}
        </div>
      ) : null}
      {!loaded ? (
        <InlineLoadingState label="Lade…" className="justify-start" />
      ) : (
        <div className="space-y-4">
            <div className="hidden sm:grid sm:grid-cols-[minmax(0,1fr)_180px_36px_32px] gap-2 px-0.5">
              <span className={SETTINGS_FIELD_LABEL}>Name</span>
              <span className={SETTINGS_FIELD_LABEL}>Pipeline</span>
              <span className={`${SETTINGS_FIELD_LABEL} text-center`}>Farbe</span>
              <span className="sr-only">Entfernen</span>
            </div>

            <ul className="space-y-1.5">
                {categories.map((c) => (
                  <li
                    key={c.id}
                    className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_180px_36px_32px] gap-1.5 sm:gap-2 sm:items-center"
                  >
                    <div className="min-w-0">
                      <span className={`sm:hidden mb-0.5 block ${SETTINGS_FIELD_LABEL}`}>
                        Name
                      </span>
                      <input
                        type="text"
                        value={c.label}
                        onChange={(e) => update(c.id, { label: e.target.value })}
                        className={SETTINGS_INPUT_CLASS}
                        placeholder="Bezeichnung"
                      />
                    </div>
                    <div className="min-w-0 w-full sm:w-[180px]">
                      <span className={`sm:hidden mb-0.5 block ${SETTINGS_FIELD_LABEL}`}>
                        Pipeline
                      </span>
                      <AppSelect
                        value={normalizePipelineId(c.pipeline_id, defaultPipelineId)}
                        onChange={(v) => update(c.id, { pipeline_id: v })}
                        options={pipelineOptions}
                        className="w-full"
                        triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                        aria-label={`Pipeline für ${c.label || 'Kategorie'}`}
                      />
                    </div>
                    <div className="flex items-center gap-2 sm:contents">
                      <div className="flex items-center gap-1.5">
                        <span className={`sm:hidden ${SETTINGS_FIELD_LABEL}`}>Farbe</span>
                        <AppSelectDropdown
                          value={c.color}
                          onChange={(v) => update(c.id, { color: v })}
                          options={colorSelectOptions}
                          placeholder="Farbe"
                          variant="icon-only"
                          className="w-9 shrink-0 min-w-0"
                          triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(c.id)}
                        disabled={categories.length <= 1}
                        className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 shrink-0 disabled:opacity-30 justify-self-center"
                        title="Kategorie entfernen"
                        aria-label="Kategorie entfernen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center gap-2 pt-0.5">
                <div className="flex min-w-[12rem] flex-1 items-center gap-2">
                  <input
                    type="text"
                    value={categoryDraft}
                    onChange={(e) => setCategoryDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      add();
                    }}
                    maxLength={120}
                    placeholder="Neue Kategorie"
                    className={`min-w-0 flex-1 ${SETTINGS_INPUT_CLASS}`}
                    disabled={categoriesAtLimit}
                  />
                  <button
                    type="button"
                    onClick={add}
                    disabled={categoriesAtLimit || !categoryDraft.trim()}
                    className={`${SETTINGS_BTN_OUTLINE} shrink-0 disabled:opacity-40`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Hinzufügen
                  </button>
                  <FeatureLimitHint
                    limitKey="leadCategories"
                    used={categories.length}
                    max={categoryLimit.max}
                    ready={limitsReady}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={saving || !isDirty || categories.length === 0}
                  className={`shrink-0 disabled:opacity-40 ${
                    isDirty
                      ? SETTINGS_BTN_PRIMARY
                      : 'inline-flex h-9 items-center px-3.5 text-sm font-medium rounded-md text-app-text-subtle bg-app-surface-muted'
                  }`}
                >
                  {saving ? 'Speichert…' : 'Speichern'}
                </button>
              </div>
        </div>
      )}
    </SettingsCard>

      <PipelineStageMapModal
        open={stageMapChanges != null && stageMapChanges.length > 0}
        changes={stageMapChanges ?? []}
        confirming={saving}
        onCancel={() => {
          if (saving) return;
          setStageMapChanges(null);
        }}
        onConfirm={(maps) => {
          void persistCategories(maps);
        }}
      />
    </>
  );
}
